import { clsx, type ClassValue } from "clsx";
import { icons } from "lucide-react";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { toSlug } from "./utils/slugify";
import { getCountries } from "@/lib/requests/countries";

// dummy comment to build

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isProd = import.meta.env.PUBLIC_VERCEL_ENV === "production";

export const seoDescriptionFrom = (description: string) =>
  description.split(". ").slice(0, 2).join(". ") + "...";

export function generateGoogleMapLink({
  lat,
  lng,
  location,
  zoom,
}: {
  lat: string;
  lng: string;
  location: string;
  zoom?: string;
}): string {
  const latQuery = `${lat},${lng}`;
  const url = `https://www.google.com/maps?ll=${encodeURIComponent(latQuery)}&q=${encodeURIComponent(location)}&z=${zoom || "14"}`;
  return url;
}

export async function getUserPosition() {
  try {
    if (!navigator || !navigator.geolocation) {
      throw new Error("This feature is available only in secure contexts.");
    }

    const position = await new Promise<GeolocationPosition>((res, rej) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          return res(position);
        },
        (e) => {
          switch (e.code) {
            case GeolocationPositionError.PERMISSION_DENIED:
              return rej(
                new Error(
                  "The acquisition of the geolocation information failed because the page don't have the necessary permissions.",
                ),
              );
            case GeolocationPositionError.POSITION_UNAVAILABLE:
              return rej(
                new Error(
                  "The acquisition of the geolocation failed because at least one internal source of position returned an internal error.",
                ),
              );
            case GeolocationPositionError.TIMEOUT:
              return rej(
                new Error(
                  "The time allowed to acquire the geolocation was reached before the information was obtained.",
                ),
              );
          }
        },
        {
          timeout: 6000,
        },
      );
    });

    return position;
  } catch (error) {
    if (error instanceof Error) {
      toast.error(error.message);
    } else {
      toast.error("Something went wrong, please try again later.");
    }

    return null;
  }
}

// Static list of 50 world's most popular travel destinations
const POPULAR_DESTINATIONS = [
  { country: "France", city: "Paris" },
  { country: "United States", city: "New York" },
  { country: "Spain", city: "Barcelona" },
  { country: "United Kingdom", city: "London" },
  { country: "Italy", city: "Rome" },
  { country: "Japan", city: "Tokyo" },
  { country: "United Arab Emirates", city: "Dubai" },
  { country: "Netherlands", city: "Amsterdam" },
  { country: "Turkey", city: "Istanbul" },
  { country: "United States", city: "Los Angeles" },
  { country: "Germany", city: "Berlin" },
  { country: "Thailand", city: "Bangkok" },
  { country: "Australia", city: "Sydney" },
  { country: "United States", city: "Miami" },
  { country: "Spain", city: "Madrid" },
  { country: "United States", city: "Las Vegas" },
  { country: "Canada", city: "Toronto" },
  { country: "United States", city: "San Francisco" },
  { country: "Singapore", city: "Singapore" },
  { country: "Austria", city: "Vienna" },
  { country: "China", city: "Hong Kong" },
  { country: "Czech Republic", city: "Prague" },
  { country: "Mexico", city: "Mexico City" },
  { country: "South Korea", city: "Seoul" },
  { country: "Portugal", city: "Lisbon" },
  { country: "Italy", city: "Florence" },
  { country: "India", city: "New Delhi" },
  { country: "United States", city: "Chicago" },
  { country: "Switzerland", city: "Zurich" },
  { country: "Brazil", city: "Rio de Janeiro" },
  { country: "Greece", city: "Athens" },
  { country: "Italy", city: "Venice" },
  { country: "Australia", city: "Melbourne" },
  { country: "United States", city: "Boston" },
  { country: "Ireland", city: "Dublin" },
  { country: "Egypt", city: "Cairo" },
  { country: "Russia", city: "Moscow" },
  { country: "Germany", city: "Munich" },
  { country: "Argentina", city: "Buenos Aires" },
  { country: "Canada", city: "Vancouver" },
  { country: "Morocco", city: "Marrakech" },
  { country: "Israel", city: "Tel Aviv" },
  { country: "Vietnam", city: "Ho Chi Minh City" },
  { country: "India", city: "Mumbai" },
  { country: "Belgium", city: "Brussels" },
  { country: "United States", city: "Washington DC" },
  { country: "South Africa", city: "Cape Town" },
  { country: "Poland", city: "Krakow" },
  { country: "China", city: "Shanghai" },
  { country: "Peru", city: "Lima" },
];

export const getRandomCountryUrl = () => {
  // Pick a random destination from the static list
  const randomIndex = Math.floor(Math.random() * POPULAR_DESTINATIONS.length);
  const destination = POPULAR_DESTINATIONS[randomIndex];

  // Return URL in the format /{country}/{city}
  return `/${toSlug(destination.country)}/${toSlug(destination.city)}`;
};

export const getBaseLinks = () =>
  [
    {
      label: "Blog",
      href: "/blog",
      iconName: "Rss",
    },
    {
      label: "Travel Guides",
      href: "/",
      iconName: "BookText",
    },
    {
      label: "Take me anywhere",
      href: "#", // This will be replaced on the client side
      iconName: "Milestone",
      isRandomLink: true,
    },
    {
      label: "Contact",
      href: "mailto:hello@9.agency",
      iconName: "Contact2",
    },
  ] as {
    label: string;
    href: string;
    iconName: keyof typeof icons;
    rel?: string;
    isRandomLink?: boolean;
  }[];

export const generateHeaderDestinationLinks = ({
  country,
  city,
}: {
  country: string;
  city?: string;
}) =>
  [
    {
      label: "Blog",
      href: "/blog",
      iconName: "Rss",
    },
    {
      label: "Travel Guides",
      href: "/",
      iconName: "BookText",
    },
    ...generateDestinationLinks({ country, city }),
  ] as {
    label: string;
    href: string;
    iconName: keyof typeof icons;
    rel?: string;
  }[];

export const generateDestinationLinks = ({
  country,
  city,
}: {
  country: string;
  city?: string;
}) => {
  const basePath = city
    ? `/${toSlug(country)}/${toSlug(city)}`
    : `/${toSlug(country)}`;

  const destination = city || country;
  return [
    {
      label: `${destination} Travel Guide`,
      href: basePath,
      iconName: "NotebookText",
    },
    {
      label: `${destination} Day Trips`,
      href: basePath + "/day-plans",
      iconName: "Milestone",
    },
    {
      label: `Tours in ${destination}`,
      href: basePath + "/adventures",
      iconName: "PersonStanding",
    },
  ] as {
    label: string;
    href: string;
    iconName: keyof typeof icons;
    rel?: string;
  }[];
};

export const generateDestinationIntroLinks = ({
  country,
  city,
}: {
  country: string;
  city?: string;
}) => {
  const basePath = city
    ? `/${toSlug(country)}/${toSlug(city)}`
    : `/${toSlug(country)}`;

  return [
    {
      label: "Day Trips",
      href: basePath + "/day-plans",
      iconName: "Milestone",
    },
    {
      label: "Adventures",
      href: basePath + "/adventures",
      iconName: "PersonStanding",
    },
  ] as {
    label: string;
    href: string;
    iconName: keyof typeof icons;
    rel?: string;
  }[];
};

export const monthInSeconds = 30 * 24 * 60 * 60;
export const weekInSeconds = 7 * 24 * 60 * 60;
export const dayInSeconds = 24 * 60 * 60;
